/**
 * Course Detail Page for trAInedup.ai
 * Displays detailed course information and enrollment interface
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Play,
  Clock,
  Users,
  BookOpen,
  Award,
  Lock,
  ArrowLeft,
  Share2,
  Heart,
  Download,
  MessageCircle,
  FileText,
  Headphones,
  Video,
  HelpCircle,
  CheckCircle,
  Star,
} from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { SubscriptionGate, FeatureBadge } from '../components/common/SubscriptionGate';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { courseService } from '../services/firebase/courses';
import { Course, Module, UserProgress, CourseDifficulty, ModuleType } from '../types/course';
import { UserRole } from '../types';
import { ROUTES } from '../utils/constants';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData, createCourseSchema, createBreadcrumbSchema } from '../components/seo/StructuredData';
import { generateMetaDescription, formatDuration as formatDurationISO } from '../utils/seoHelpers';

/**
 * Course detail page component
 * Shows comprehensive course information and enrollment options
 */
export const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, hasRole } = useAuth();
  const { canAccessTier, getCurrentTier } = useSubscription();
  
  // State
  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'curriculum' | 'reviews'>('overview');
  const [isFavorite, setIsFavorite] = useState(false);

  /**
   * Load course data
   */
  useEffect(() => {
    const loadCourseData = async () => {
      if (!courseId) return;

      try {
        setLoading(true);

        // Load course details
        const courseData = await courseService.getCourse(courseId);
        if (!courseData) {
          navigate(ROUTES.courses);
          return;
        }

        // Check if user can access unpublished courses
        const canAccessUnpublished = hasRole(UserRole.ADMIN) || hasRole(UserRole.INSTRUCTOR);
        if (!courseData.isPublished && !canAccessUnpublished) {
          navigate(ROUTES.courses);
          return;
        }

        setCourse(courseData);

        const modulesPromise = courseService.getCourseModules(courseId);

        if (isAuthenticated && user) {
          const [modulesData, progressData, favorites] = await Promise.all([
            modulesPromise,
            courseService.getUserProgress(courseId),
            courseService.getUserFavorites(),
          ]);

          setModules(modulesData);
          setUserProgress(progressData);
          setIsFavorite(favorites.some(fav => fav.courseId === courseId));
        } else {
          const modulesData = await modulesPromise;
          setModules(modulesData);
        }
      } catch (error) {
        console.error('Error loading course data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [courseId, isAuthenticated, user, navigate, hasRole]);

  /**
   * Handle course enrollment
   */
  const handleEnroll = async () => {
    if (!course) {
      return;
    }
    if (!isAuthenticated) {
      return;
    }

    try {
      setEnrolling(true);
      
      // Enroll in course
      await courseService.enrollInCourse(course.id);

      // Navigate immediately to first module if available
      if (modules.length > 0) {
        navigate(`/courses/${course.id}/modules/${modules[0].id}`);
      } else {
        console.error('No modules found for course:', course.id);
        setEnrolling(false);
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      setEnrolling(false);
    }
  };

  /**
   * Handle favorite toggle
   */
  const handleFavoriteToggle = async () => {
    if (!course || !isAuthenticated) return;

    try {
      if (isFavorite) {
        await courseService.removeFavorite(course.id);
        setIsFavorite(false);
      } else {
        await courseService.addFavorite(course.id);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  /**
   * Handle sharing course details
   */
  const handleShare = async () => {
    if (!course) return;

    const shareData = {
      title: course.title,
      text: course.shortDescription || course.description || 'Check out this course on trAInedup.ai',
      url: window.location.href,
    };

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        console.error('Error using native share:', error);
      }
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareData.url);
        alert('Course link copied to clipboard!');
      } else {
        window.prompt('Copy this course link', shareData.url);
      }
    } catch (error) {
      console.error('Error sharing course link:', error);
      window.prompt('Copy this course link', shareData.url);
    }
  };

  /**
   * Get difficulty badge color
   */
  const getDifficultyColor = (difficulty: CourseDifficulty) => {
    switch (difficulty) {
      case CourseDifficulty.BEGINNER:
        return 'bg-green-100 text-green-800 border-green-200';
      case CourseDifficulty.INTERMEDIATE:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case CourseDifficulty.ADVANCED:
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  /**
   * Format duration
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  /**
   * Get module type icon
   */
  const getModuleTypeIcon = (type: ModuleType) => {
    switch (type) {
      case ModuleType.VIDEO:
        return <Video className="w-4 h-4" />;
      case ModuleType.AUDIO:
        return <Headphones className="w-4 h-4" />;
      case ModuleType.PDF:
        return <FileText className="w-4 h-4" />;
      case ModuleType.TEXT:
        return <BookOpen className="w-4 h-4" />;
      case ModuleType.QUIZ:
        return <HelpCircle className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  /**
   * Check if user can access course
   */
  const canAccessCourse = () => {
    if (!course) return false;
    // Check tier access based on requiredSubscriptionTier
    // A course is free if requiredSubscriptionTier is 'free'
    const tierToCheck = course.requiredSubscriptionTier || 'free';
    return canAccessTier(tierToCheck as any);
  };

  /**
   * Get enrollment button content
   */
  const getEnrollmentButton = () => {
    if (!isAuthenticated) {
      return (
        <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ROUTES.login)}>
          Sign In to Enroll
        </Button>
      );
    }

    if (userProgress) {
      // Check if course is completed
      const isCompleted = userProgress.completionPercentage === 100 || userProgress.status === 'completed';
      
      if (!isCompleted) {
        return (
          <Button 
            variant="primary" 
            size="lg" 
            fullWidth
            onClick={async () => {
              // Navigate to first uncompleted module
              const firstUncompleted = await courseService.getFirstUncompletedModule(course?.id || '');
              if (firstUncompleted) {
                navigate(`/courses/${course?.id}/modules/${firstUncompleted.id}`);
              } else if (modules.length > 0) {
                navigate(`/courses/${course?.id}/modules/${modules[0].id}`);
              }
            }}
          >
            Continue Learning
          </Button>
        );
      }
      // If completed, show Restart Course button
      return (
        <Button 
          variant="primary" 
          size="lg" 
          fullWidth
          loading={enrolling}
          onClick={async () => {
            try {
              setEnrolling(true);
              // Reset course progress
              await courseService.resetCourseProgress(course?.id || '');
              // Re-enroll in the course
              await courseService.enrollInCourse(course?.id || '');
              // Navigate to first module
              if (modules.length > 0) {
                navigate(`/courses/${course?.id}/modules/${modules[0].id}`);
              }
            } catch (error) {
              console.error('Error restarting course:', error);
              setEnrolling(false);
            }
          }}
        >
          Restart Course
        </Button>
      );
    }

    if (!canAccessCourse()) {
      // Debug logging
      const currentTier = getCurrentTier();
      // eslint-disable-next-line no-console
      console.log('DEBUG - Enrollment Button:', {
        canAccess: canAccessCourse(),
        currentTier,
        requiredTier: course?.requiredSubscriptionTier,
        isFree: course?.isFree,
        isAuthenticated,
      });
      
      return (
        <SubscriptionGate
          requiredTier={course?.requiredSubscriptionTier as any}
          showUpgrade={false}
          fallback={
            <Button 
              variant="primary" 
              size="lg" 
              fullWidth 
              onClick={() => navigate('/pricing')}
            >
              Upgrade to Start
            </Button>
          }
        >
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={enrolling}
            onClick={handleEnroll}
          >
            Start Course
          </Button>
        </SubscriptionGate>
      );
    }

    return (
      <Button
        variant="primary"
        size="lg"
        fullWidth
        loading={enrolling}
        onClick={handleEnroll}
      >
        {course?.isFree ? 'Start Free' : 'Start Course'}
      </Button>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Course Not Found</h1>
          <Button variant="outline" onClick={() => navigate(ROUTES.courses)}>
            Back to Courses
          </Button>
        </div>
      </Layout>
    );
  }

  // Generate SEO metadata
  const pageTitle = `${course.title} - AI Course`;
  const pageDescription = generateMetaDescription(
    course.description || `Learn ${course.title} with trAInedup.ai. Practical AI training with hands-on exercises and expert guidance.`,
    160
  );
  const courseUrl = `https://trainedup.ai/courses/${courseId}`;

  // Calculate total duration in minutes
  const totalMinutes = modules.reduce((sum, module) => sum + (module.estimatedDuration || 0), 0);

  // Generate Course schema
  const courseSchema = createCourseSchema({
    name: course.title,
    description: course.description || pageDescription,
    provider: 'trAInedup.ai',
    url: courseUrl,
    image: course.thumbnail || 'https://trainedup.ai/tulogo.png',
    numberOfLessons: modules.length,
    timeRequired: totalMinutes > 0 ? formatDurationISO(totalMinutes) : undefined,
    courseCode: courseId,
    educationalLevel: course.difficulty || 'Beginner to Advanced',
  });

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://trainedup.ai/' },
    { name: 'Courses', url: 'https://trainedup.ai/courses' },
    { name: course.title, url: courseUrl },
  ]);

  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={courseUrl}
        ogType="article"
        ogImage={course.thumbnail || 'https://trainedup.ai/tulogo.png'}
        ogImageAlt={`${course.title} course thumbnail`}
        keywords={[
          course.title,
          'AI course',
          'AI training',
          course.difficulty || 'AI learning',
          'online AI course',
        ]}
      />
      <StructuredData data={courseSchema} />
      <StructuredData data={breadcrumbSchema} />

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="outline"
            size="sm"
            icon={ArrowLeft}
            onClick={() => navigate(ROUTES.courses)}
            className="mb-6"
          >
            Back to Courses
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Course Header */}
              <div className="mb-8">
                {/* Course Video/Image */}
                <div className="aspect-video bg-gradient-to-br from-primary-100 to-secondary-100 rounded-lg overflow-hidden mb-6 relative">
                  {course.coverImage ? (
                    <img
                      src={course.coverImage}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-primary-400" />
                    </div>
                  )}
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all cursor-pointer">
                      <Play className="w-8 h-8 text-primary ml-1" />
                    </div>
                  </div>

                  {/* Access Badge */}
                  {!canAccessCourse() && (
                    <div className="absolute top-4 right-4">
                      <FeatureBadge tier={course.requiredSubscriptionTier as any} />
                    </div>
                  )}
                </div>

                {/* Course Meta */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="text-sm font-medium text-primary">
                    {course.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border capitalize ${getDifficultyColor(course.difficulty)}`}>
                    {course.difficulty}
                  </span>
                  {course.isFree && (
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Free
                    </span>
                  )}
                </div>

                {/* Course Title */}
                <h1 className="font-montserrat font-bold text-[24px] text-neutral-800 mb-4">
                  {course.title}
                </h1>

                {/* About Section */}
                <div className="mb-6">
                  <h2 className="font-montserrat font-bold text-[18px] text-neutral-800 mb-2">
                    About This Course
                  </h2>
                  <p className="font-open-sans font-normal text-[16px] text-neutral-600 leading-relaxed">
                    {course.description}
                  </p>
                </div>

                {/* Course Stats */}
                <div className="flex flex-wrap items-center gap-6 text-neutral-600 p-4 bg-neutral-50 rounded-2xl mb-6">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-primary" />
                    <span className="font-medium">{formatDuration(course.estimatedDuration)}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 mr-2 text-primary" />
                    <span className="font-medium">{course.enrollmentCount} students</span>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-primary" />
                    <span className="font-medium">{course.totalModules} modules</span>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-neutral-200 mb-8">
                <nav className="flex space-x-8">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'curriculum', label: 'Curriculum' },
                    { id: 'reviews', label: 'Reviews' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-montserrat font-semibold text-2xl text-neutral-800 mb-4">
                      Course Overview
                    </h2>
                    <div className="prose prose-neutral max-w-none">
                      <p className="text-neutral-700 leading-relaxed">
                        {course.shortDescription && course.shortDescription.trim() !== course.description.trim()
                          ? course.shortDescription
                          : 'Explore the curriculum, module formats, and student reviews to get a comprehensive look at what this course offers.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'curriculum' && (
                <div>
                  <h2 className="font-montserrat font-semibold text-2xl text-neutral-800 mb-6">
                    Course Curriculum
                  </h2>
                  <div className="space-y-4">
                    {modules.map((module, moduleIndex) => {
                      // Find progress for this module
                      const progress = userProgress?.moduleProgress?.find(mp => mp.moduleId === module.id);
                      const isCompleted = progress?.status === 'completed';
                      // Highlight if this is the current module (URL param not available here, so highlight first incomplete or last completed)
                      let isCurrent = false;
                      if (userProgress?.moduleProgress) {
                        const firstIncomplete = userProgress.moduleProgress.find(mp => mp.status !== 'completed');
                        if (firstIncomplete) {
                          isCurrent = firstIncomplete.moduleId === module.id;
                        } else {
                          // All complete, highlight last
                          isCurrent = userProgress.moduleProgress[userProgress.moduleProgress.length - 1]?.moduleId === module.id;
                        }
                      }
                      return (
                        <Card
                          key={module.id}
                          className={`overflow-hidden border-2 transition-all ${isCurrent ? 'border-primary shadow-lg' : 'border-transparent'}`}
                        >
                          <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="font-montserrat font-normal text-[20px] text-neutral-800 flex items-center gap-2">
                                Module {moduleIndex + 1}: {module.title}
                                {isCompleted && <CheckCircle className="w-5 h-5 text-green-500 ml-2" />}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-neutral-500">
                                <div className="flex items-center">
                                  {getModuleTypeIcon(module.type)}
                                  <span className="ml-1 capitalize">{module.type}</span>
                                </div>
                                <span>{formatDuration(module.estimatedDuration)}</span>
                              </div>
                            </div>
                            <p className="font-open-sans font-normal text-[16px] text-neutral-600 mb-4">{module.description}</p>
                            {/* Module Access Indicator */}
                            <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mr-3">
                                  {canAccessCourse() || course.isFree ? (
                                    getModuleTypeIcon(module.type)
                                  ) : (
                                    <Lock className="w-4 h-4 text-neutral-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-montserrat font-normal text-[20px] text-neutral-800">
                                    {module.title}
                                  </p>
                                  <p className="text-sm text-neutral-500">
                                    {module.type.charAt(0).toUpperCase() + module.type.slice(1)} Module • {formatDuration(module.estimatedDuration)}
                                  </p>
                                </div>
                              </div>
                              {(canAccessCourse() || course.isFree) && userProgress && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  {isCompleted ? 'Completed' : isCurrent ? 'Current' : 'Available'}
                                </span>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                    
                    {/* Virtual Course Review Module */}
                    <Card className="overflow-hidden border-2 border-transparent">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-montserrat font-normal text-[20px] text-neutral-800 flex items-center gap-2">
                            Module {modules.length + 1}: Course Review
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-neutral-500">
                            <div className="flex items-center">
                              <Star className="w-4 h-4" />
                              <span className="ml-1">Review</span>
                            </div>
                            <span>5 min</span>
                          </div>
                        </div>
                        <p className="font-open-sans font-normal text-[16px] text-neutral-600 mb-4">
                          Share your experience and feedback to help us improve this course and earn your certificate.
                        </p>
                        {/* Module Access Indicator */}
                        <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mr-3">
                              <Star className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-montserrat font-normal text-[20px] text-neutral-800">
                                Course Review
                              </p>
                              <p className="text-sm text-neutral-500">
                                Survey • ~5 minutes
                              </p>
                            </div>
                          </div>
                          {userProgress && userProgress.completionPercentage === 100 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Final Step
                            </span>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <h2 className="font-montserrat font-semibold text-2xl text-neutral-800 mb-6">
                    Student Reviews
                  </h2>
                  <div className="text-center py-8 text-neutral-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                    <p>Reviews feature coming soon</p>
                  </div>
                </div>
              )}
              {/* Topics */}
              {course.tags.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-montserrat font-semibold text-2xl text-neutral-800 mb-4">
                    Topics Covered
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {course.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                {/* Enrollment Card */}
                <Card className="p-6">
                  <div className="text-center mb-6">
                    {course.isFree ? (
                      <div className="text-3xl font-bold text-green-600 mb-2">Free</div>
                    ) : (
                      <div className="text-3xl font-bold text-neutral-800 mb-2">
                        Premium Course
                      </div>
                    )}
                    <p className="text-sm text-neutral-600">
                      {userProgress ? 'Continue your learning journey' : 'Start learning today'}
                    </p>
                  </div>

                  {getEnrollmentButton()}

                  {/* Course Actions */}
                  <div className="flex justify-center space-x-4 mt-4">
                    <button 
                      onClick={handleFavoriteToggle}
                      className="p-2 text-neutral-500 hover:text-neutral-700"
                      disabled={!isAuthenticated}
                    >
                      <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : ''}`} />
                    </button>
                    <button 
                      className="p-2 text-neutral-500 hover:text-neutral-700"
                      onClick={handleShare}
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                </Card>

                {/* Course Includes */}
                <Card className="p-6">
                  <h3 className="font-semibold text-lg text-neutral-800 mb-4">
                    This course includes:
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-neutral-500 mr-3" />
                      <span className="text-neutral-700">{formatDuration(course.estimatedDuration)} of content</span>
                    </div>
                    <div className="flex items-center">
                      <BookOpen className="w-5 h-5 text-neutral-500 mr-3" />
                      <span className="text-neutral-700">{course.totalModules} modules + review</span>
                    </div>
                    <div className="flex items-center">
                      <Award className="w-5 h-5 text-neutral-500 mr-3" />
                      <span className="text-neutral-700">Certificate of completion</span>
                    </div>
                    <div className="flex items-center">
                      <Download className="w-5 h-5 text-neutral-500 mr-3" />
                      <span className="text-neutral-700">Downloadable resources</span>
                    </div>
                  </div>
                </Card>

                {/* Sidebar Module List (with checkmarks and highlight) */}
                {modules.length > 0 && (
                  <Card className="p-6">
                    <h3 className="font-semibold text-lg text-neutral-800 mb-4">Course Modules</h3>
                    <div className="flex flex-col gap-3">
                      {modules.map((module, idx) => {
                        const progress = userProgress?.moduleProgress?.find(mp => mp.moduleId === module.id);
                        const isCompleted = progress?.status === 'completed';
                        let isCurrent = false;
                        if (userProgress?.moduleProgress) {
                          const firstIncomplete = userProgress.moduleProgress.find(mp => mp.status !== 'completed');
                          if (firstIncomplete) {
                            isCurrent = firstIncomplete.moduleId === module.id;
                          } else {
                            isCurrent = userProgress.moduleProgress[userProgress.moduleProgress.length - 1]?.moduleId === module.id;
                          }
                        }
                        return (
                          <div
                            key={module.id}
                            className={`flex items-center justify-between rounded-xl px-4 py-3 transition-all cursor-pointer ${isCurrent ? 'bg-primary-50 border border-primary/30 text-primary font-semibold shadow-sm' : 'hover:bg-neutral-50'} ${isCompleted ? '' : ''}`}
                            onClick={() => navigate(`/courses/${course.id}/modules/${module.id}`)}
                          >
                            <div className="flex items-center gap-2">
                              {getModuleTypeIcon(module.type)}
                              <span
                                className={`text-base ${isCurrent ? 'text-primary font-semibold' : 'text-neutral-700 font-medium'}`}
                              >
                                {idx + 1}. {module.title}
                              </span>
                            </div>
                            {isCompleted && (
                              <CheckCircle className={`w-6 h-6 ${isCurrent ? 'text-primary' : 'text-blue-400'}`} />
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Virtual Course Review Module */}
                      <div
                        className="flex items-center justify-between rounded-xl px-4 py-3 transition-all hover:bg-neutral-50"
                      >
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4" />
                          <span className="text-base text-neutral-700 font-medium">
                            {modules.length + 1}. Course Review
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
                {/* Progress Card (if enrolled) */}
                {userProgress && (
                  <Card className="p-6">
                    <h3 className="font-semibold text-lg text-neutral-800 mb-4">
                      Your Progress
                    </h3>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-neutral-600 mb-2">
                        <span>Completion</span>
                        <span>{Math.round(userProgress.completionPercentage)}%</span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${userProgress.completionPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-sm text-neutral-600">
                      <p>Modules completed: {userProgress.completedModules} / {userProgress.totalModules}</p>
                      <p>Time spent: {formatDuration(userProgress.totalTimeSpent)}</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
